package presentation.screens.otp

import analytics.Analytics
import androidx.lifecycle.SavedStateHandle
import androidx.navigation.toRoute
import com.kinoshazam.Navigator
import com.kinoshazam.analytics.AppEvents
import com.kinoshazam.data.PendingAuthCredentials
import com.kinoshazam.data.TokenRepository
import com.kinoshazam.data.models.LoginResult
import com.kinoshazam.data.models.VerifyEmailResult
import com.kinoshazam.data.models.VerifyOtpResult
import com.kinoshazam.model.Route
import data.LoginRepository
import kotlinx.coroutines.delay
import org.koin.core.component.KoinComponent
import presentation.screens.otp.OtpState.OtpFlowType
import utils.BaseViewModel
import utils.launch

class OtpViewModel(
    private val loginRepository: LoginRepository,
    private val tokenRepository: TokenRepository,
    private val pendingAuthCredentials: PendingAuthCredentials,
    private val navigator: Navigator,
    private val analytics: Analytics,
    savedStateHandle: SavedStateHandle,
) : BaseViewModel<OtpState, OtpMVIAction>(), KoinComponent {

    private val route = savedStateHandle.toRoute<Route.Dialog.OtpScreen>()
    private val email = route.email
    private val flowType = route.flowType

    init {
        initTimer()
    }

    override fun initState(): OtpState = OtpState(
        otp = OtpState.initOtp,
        remainingTime = 30,
        flowType = OtpFlowType.valueOf(flowType),
    )

    override fun onAction(action: OtpMVIAction) {
        when (action) {
            is OtpMVIAction.ResendOtp -> onResendOtpClicked()
            is OtpMVIAction.OtpChanged -> onOtpChanged(action.value, action.index)
            is OtpMVIAction.BackClicked -> navigator.back()
        }
    }

    private fun onOtpChanged(newValue: String, newIndex: Int) {
        if (stateValue.isLoading) return
        updateState {
            val newOtp = otp.mapIndexed { index, value ->
                if (index == newIndex) {
                    newValue
                } else {
                    value
                }
            }
            if (newOtp.all { it.length == 1 }) {
                submitOtp(newOtp.joinToString(separator = ""))
            }
            copy(
                isError = false,
                otp = newOtp,
            )
        }
    }

    private fun submitOtp(code: String) {
        if (stateValue.isLoading) return
        updateState { copy(isLoading = true, isError = false) }
        launch(onError = {
            updateState {
                copy(
                    isLoading = false,
                    isError = true,
                    otp = OtpState.initOtp,
                )
            }
        }) {
            when (stateValue.flowType) {
                OtpFlowType.Verification -> {
                    val result = loginRepository.verifyEmailOtp(
                        email = email,
                        otp = code,
                    ).result
                    when (result) {
                        is VerifyEmailResult.Success -> onEmailVerified()
                        else -> updateState {
                            copy(isLoading = false, isError = true, otp = OtpState.initOtp)
                        }
                    }
                }

                OtpFlowType.Password -> {
                    val result = loginRepository.verifyPasswordOtp(
                        email = email,
                        otp = code,
                    ).result
                    when (result) {
                        is VerifyOtpResult.Success -> {
                            updateState { copy(isLoading = false) }
                            navigator.navigateTo(
                                Route.Dialog.ChangePassword(
                                    token = result.verifyToken
                                )
                            )
                        }

                        else -> updateState {
                            copy(isLoading = false, isError = true, otp = OtpState.initOtp)
                        }
                    }
                }
            }
        }
    }

    /**
     * verify-email does not return tokens — establish a session via login when password is available.
     */
    private suspend fun onEmailVerified() {
        val pwd = pendingAuthCredentials.takePassword(email)
        if (pwd.isNullOrEmpty()) {
            updateState { copy(isLoading = false) }
            navigator.navigateTo(Route.Dialog.Login, isClearStack = true)
            return
        }
        when (val loginResult = loginRepository.login(email, pwd).result) {
            is LoginResult.LoggedIn -> {
                tokenRepository.saveTokens(
                    access = loginResult.accessToken,
                    refresh = loginResult.refreshToken,
                )
                analytics.track(AppEvents.loginSucceeded())
                updateState { copy(isLoading = false) }
                navigator.navigateTo(Route.Dialog.Profile, isClearStack = true)
            }
            else -> {
                updateState { copy(isLoading = false) }
                navigator.navigateTo(Route.Dialog.Login, isClearStack = true)
            }
        }
    }

    private fun onResendOtpClicked() {
        if (stateValue.isLoading || stateValue.remainingTime > 0) return
        updateState { copy(remainingTime = 30, isError = false) }
        initTimer()
        launch(onError = {
            updateState { copy(isError = true) }
        }) {
            when (stateValue.flowType) {
                OtpFlowType.Verification -> loginRepository.requestOtp(email = email)
                OtpFlowType.Password -> loginRepository.requestPasswordReset(email = email)
            }
        }
    }

    private fun initTimer() {
        launch {
            while (state.value.remainingTime > 0) {
                delay(1000)
                updateState { copy(remainingTime = remainingTime - 1) }
            }
        }
    }
}
